import { useState } from "react";
import {
  Callout,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  FieldLabel,
  ReasonField,
  TextInput,
  formatDate,
  formatDateTime,
  isApiError,
  useCommandMutation,
  useReason,
  useToast,
} from "@amber/ui";
import type { DependencyKind } from "../../contracts/index";
import { operationsCommands } from "../queries";
import { AFTER_OPERATIONS_CHANGE, AFTER_OPERATIONS_CONFLICT } from "../keys";

export interface DateTarget {
  readonly kind: DependencyKind;
  readonly key: string;
  readonly label: string;
  readonly date: string | null;
  readonly revision: number;
}

const WORDS = {
  credential: { title: "Record expiry", field: "Expires on", none: "No expiry", saved: "Expiry recorded" },
  licence: { title: "Record renewal", field: "Renews on", none: "No renewal date", saved: "Renewal recorded" },
} as const;

export function RecordDateDialog({
  target,
  onClose,
  onRecorded,
}: {
  target: DateTarget;
  onClose: () => void;
  onRecorded?: (() => void) | undefined;
}) {
  const words = WORDS[target.kind];
  const toast = useToast();
  const [none, setNone] = useState(target.date === null);
  const [date, setDate] = useState(target.date ?? "");
  const mutation = useCommandMutation({
    run: (csrf, reason: string) =>
      operationsCommands.recordDependencyDate(
        target.kind,
        target.key,
        { date: none ? null : date, reason, base_revision: target.revision },
        csrf,
      ),
    invalidate: AFTER_OPERATIONS_CHANGE,
    invalidateOnConflict: AFTER_OPERATIONS_CONFLICT,
    onSuccess: () => {
      toast.show(words.saved);
      onRecorded?.();
      onClose();
    },
  });
  const conflict = isApiError(mutation.error) && mutation.error.code === "conflict" ? mutation.error : null;
  const fieldErrors = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const reason = useReason(fieldErrors["reason"]);
  const dateError = reason.submitted && !none && date === "" ? `Enter a date or choose “${words.none}”` : null;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={words.title}
      onSubmit={() => {
        if (reason.submit() && (none || date !== "")) mutation.mutate(reason.trimmed);
      }}
      footer={
        <DialogFooter
          onClose={onClose}
          confirm={words.title}
          pending={mutation.isPending}
          failed={mutation.isError && !conflict}
        />
      }
    >
      <DialogText>
        <b className="font-mono">{target.label}</b> · now{" "}
        {target.date ? formatDate(`${target.date}T12:00:00Z`) : words.none.toLowerCase()}. Only the date is kept; Amber
        never stores or shows a credential's value.
      </DialogText>
      <label className="mt-3 flex items-center gap-2 text-ui">
        <input
          type="checkbox"
          checked={none}
          onChange={(e) => {
            setNone(e.target.checked);
          }}
        />
        <span>{words.none}</span>
      </label>
      {none ? null : (
        <div className="mt-3">
          <FieldLabel htmlFor="dependency-date" required>
            {words.field}
          </FieldLabel>
          <TextInput
            id="dependency-date"
            type="date"
            value={date}
            aria-invalid={dateError ? true : undefined}
            onChange={(e) => {
              setDate(e.target.value);
            }}
          />
          {dateError ? <p className="m-0 mt-1 text-md text-attention">{dateError}</p> : null}
        </div>
      )}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      {conflict ? (
        <Callout tone="attention" role="alert" className="mt-3" title="Someone recorded a date since you opened it">
          {conflict.changedBy ? `${conflict.changedBy} recorded a new date` : "A new date was recorded"}
          {conflict.changedAt ? ` at ${formatDateTime(conflict.changedAt)}` : ""}. Close this dialog to see it.
        </Callout>
      ) : (
        <FailedCallout error={mutation.error} fieldsShown />
      )}
    </Dialog>
  );
}
